import {noop} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf, Renderer, RendererOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {PopoverTransferProps} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';
import {SettingsObjectPickerHeader} from './settingsObjectsPickerHeader';

/*
 * Props.
 */

type ItemId = number;
interface ObjectsPickerItem<T> {
  id: ItemId;
  item: T;
}
export interface SettingsObjectsPickerProps<T> {
  className?: string;
  isReadonly?: boolean;
  headersCanCollapse?: boolean;
  columns: ReadonlyArray<ReactNode>;
  onHeaderChange: Handler;
  renderHeaderDropdownTitle?: Renderer;
  renderHeaderDropdownLabel?: Renderer;
  renderHeaderDropdown?: RendererOf<PopoverTransferProps>;
  isSubHeaderChecked?: boolean;
  subHeaderLabel?: ReactNode;
  onSubHeaderChange?: Handler;
  renderItem: (index: number, item: T) => ReactNode;
  renderRightItem?: (index: number, item: T) => ReactNode;
  items: ReadonlyArray<ObjectsPickerItem<T>>;
  selectedIds: ReadonlyArray<ItemId>;
  onChange: HandlerOf<T>;
  isHeaderChecked?: boolean;
  isHeaderIndeterminate?: boolean;
}

/*
 * Style.
 */

export const StyledSettingsObjectsPickerTable = styled.div`
  border-radius: 12px;
  width: 100%;
  border: 1px solid #e0e0e0;
  background: ${(p) => p.theme.greys.white};
  overflow: hidden;
`;

const StyledTableBodyDiv = styled.div``;

const StyledTableCellDiv = styled.div`
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }
`;

const StyledTableCellCheckboxWrapper = styled.div`
  padding: 7px 16px 8px;
  display: flex;
`;

const StyledTableCellCheckboxLabel = styled.div`
  width: 100%;
  margin-left: 16px;

  display: inline-grid;
  grid-column-gap: 16px;
  align-items: center;
  grid-template-columns: 1fr auto;
  grid-template-areas: 'content right';
`;

const StyledTableCellWithColumns = styled.div<{$columns: number}>`
  display: inline-grid;
  grid-column-gap: 4px;
  grid-template-columns: repeat(${(p) => p.$columns}, 1fr);
`;

const StyledTableBodyCellWithColumns = styled(StyledTableCellWithColumns)`
  width: 100%;
  margin-left: 16px;
  padding: 7px 0px;
`;

interface TableCellStyleProps {
  $isSelected: boolean;
  $isFirst: boolean;
}

const StyledTableCell = styled.div<TableCellStyleProps>`
  font-weight: ${(p) => (p.$isSelected ? fontWeights.semibold : fontWeights.medium)};
  font-size: ${fontSizes.medium};
  line-height: 17px;
  color: ${(p) => (!p.$isSelected && !p.$isFirst ? p.theme.greys.shade70 : p.theme.greys.shade80)};
  ${ellipsis()};
`;

const StyledTableCellContent = styled(StyledTableCell)`
  grid-area: content;
`;

const StyledTableCellRight = styled.div`
  grid-area: right;
  padding: 15px 16px;
  padding-right: 0;
  padding-left: 16px;
`;

/*
 * Component.
 */

export const SettingsObjectsPicker = <T,>(props: SettingsObjectsPickerProps<T>): ReturnType<FC> => {
  const {renderItem, renderRightItem, items, selectedIds, onChange, columns} = props;

  const isHeaderChecked = props.isHeaderChecked ?? (items.length > 0 && selectedIds.length === items.length);
  const isHeaderIndeterminate =
    (props.isHeaderIndeterminate ??
      (items.length > 0 && selectedIds.length > 0 && selectedIds.length < items.length)) &&
    !isHeaderChecked;

  return (
    <StyledSettingsObjectsPickerTable className={props.className}>
      <SettingsObjectPickerHeader
        columns={columns}
        isHeaderChecked={isHeaderChecked}
        isHeaderIndeterminate={isHeaderIndeterminate}
        isSubHeaderChecked={props.isSubHeaderChecked}
        onHeaderChange={props.onHeaderChange}
        onSubHeaderChange={props.onSubHeaderChange}
        renderHeaderDropdown={props.renderHeaderDropdown}
        renderHeaderDropdownLabel={props.renderHeaderDropdownLabel}
        renderHeaderDropdownTitle={props.renderHeaderDropdownTitle}
        subHeaderLabel={props.subHeaderLabel}
      />
      <StyledTableBodyDiv>
        {items.map((pickerItem) => (
          <StyledTableCellDiv
            key={pickerItem.id}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(pickerItem.item);
            }}
          >
            <StyledTableCellCheckboxWrapper>
              <CheckBox isChecked={selectedIds.includes(pickerItem.id)} onChange={noop} />
              {renderSettingsObjectsPickerColumns(
                pickerItem,
                columns,
                selectedIds,
                renderItem,
                renderRightItem,
              )}
            </StyledTableCellCheckboxWrapper>
          </StyledTableCellDiv>
        ))}
      </StyledTableBodyDiv>
    </StyledSettingsObjectsPickerTable>
  );
};

/*
 * Helpers.
 */

export function renderSettingsObjectsPickerColumns<T>(
  pickerItem: ObjectsPickerItem<T>,
  columns: ReadonlyArray<ReactNode>,
  selectedIds: ReadonlyArray<ItemId>,
  renderItem: (index: number, item: T) => ReactNode,
  renderRightItem?: (index: number, item: T) => ReactNode,
) {
  if (columns.length === 1) {
    return (
      <StyledTableCellCheckboxLabel>
        <StyledTableCellContent $isFirst $isSelected={selectedIds.includes(pickerItem.id)}>
          {renderItem(0, pickerItem.item)}
        </StyledTableCellContent>
        <StyledTableCellRight>{renderRightItem && renderRightItem(0, pickerItem.item)}</StyledTableCellRight>
      </StyledTableCellCheckboxLabel>
    );
  }

  return (
    <StyledTableBodyCellWithColumns $columns={columns.length}>
      {columns.map((_, index) => (
        <StyledTableCell
          $isFirst={index === 0}
          $isSelected={selectedIds.includes(pickerItem.id)}
          // eslint-disable-next-line react/no-array-index-key
          key={`col-${index}`}
        >
          {renderItem(index, pickerItem.item)}
        </StyledTableCell>
      ))}
    </StyledTableBodyCellWithColumns>
  );
}
